import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { uploadToSeaweedFS } from '@/lib/seaweedfs';

// ─────────────────────────────────────────────────────────────
// useZCallRecorder — enregistrement de la réunion (vidéo + audio).
//
// Compose un « canvas » qui rassemble les flux vidéo de tous les participants
// (grille) et mélange tous les flux audio via l'API Web Audio. Le résultat est
// encodé en WebM (VP9/VP8 + Opus, ~2,5 Mbps) puis compressé et envoyé dans le
// dossier Médias de l'hôte (/dashboard/media), où il reste disponible.
// ─────────────────────────────────────────────────────────────

export interface RecorderSource {
  stream: MediaStream | null;
  name: string;
}

export interface UseZCallRecorderResult {
  recording: boolean;
  elapsed: number;
  error: string | null;
  start: (sources: RecorderSource[]) => void;
  stop: () => Promise<Blob | null>;
  upload: (
    blob: Blob,
    userId: number,
    title: string,
    opts?: { roomId?: string; meetingId?: number | null; durationSeconds?: number },
  ) => Promise<string | null>;
}

const WIDTH = 1280;
const HEIGHT = 720;

function pickMime(): string {
  const candidates = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
  ];
  for (const c of candidates) {
    try {
      if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(c)) return c;
    } catch {
      /* type non supporté */
    }
  }
  return 'video/webm';
}

export function useZCallRecorder(): UseZCallRecorderResult {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const videosRef = useRef<Array<{ el: HTMLVideoElement; name: string }>>([]);
  const rafRef = useRef<number | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const stopResolveRef = useRef<((blob: Blob | null) => void) | null>(null);
  const timerRef = useRef<number | null>(null);

  const cleanup = useCallback(() => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (timerRef.current != null) window.clearInterval(timerRef.current);
    timerRef.current = null;
    videosRef.current.forEach(({ el }) => { try { el.pause(); el.srcObject = null; } catch { /* ignore */ } });
    videosRef.current = [];
    try { audioCtxRef.current?.close(); } catch { /* ignore */ }
    audioCtxRef.current = null;
    canvasRef.current = null;
    recorderRef.current = null;
    setRecording(false);
    setElapsed(0);
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  const drawFrame = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.fillStyle = '#0b0b0f';
    ctx.fillRect(0, 0, WIDTH, HEIGHT);

    const tiles = videosRef.current;
    const n = Math.max(1, tiles.length);
    const cols = n <= 1 ? 1 : n <= 4 ? 2 : 3;
    const rows = Math.ceil(n / cols);
    const tw = WIDTH / cols;
    const th = HEIGHT / rows;

    tiles.forEach((tile, i) => {
      const cx = (i % cols) * tw;
      const cy = Math.floor(i / cols) * th;
      const el = tile.el;
      try {
        if (el.videoWidth > 0 && el.videoHeight > 0) {
          const scale = Math.max(tw / el.videoWidth, th / el.videoHeight);
          const dw = el.videoWidth * scale;
          const dh = el.videoHeight * scale;
          ctx.save();
          ctx.beginPath();
          ctx.rect(cx, cy, tw, th);
          ctx.clip();
          ctx.drawImage(el, cx + (tw - dw) / 2, cy + (th - dh) / 2, dw, dh);
          ctx.restore();
        } else {
          ctx.fillStyle = '#1b1b22';
          ctx.fillRect(cx, cy, tw, th);
        }
      } catch {
        /* flux non dessinable */
      }
      // Étiquette du participant
      ctx.fillStyle = 'rgba(0,0,0,0.55)';
      ctx.fillRect(cx + 12, cy + th - 40, Math.min(tw - 24, ctx.measureText(tile.name).width + 80), 28);
      ctx.fillStyle = '#ffffff';
      ctx.font = '16px sans-serif';
      ctx.fillText(tile.name, cx + 24, cy + th - 20);
    });

    rafRef.current = requestAnimationFrame(drawFrame);
  }, []);

  const start = useCallback((sources: RecorderSource[]) => {
    setError(null);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = WIDTH;
      canvas.height = HEIGHT;
      canvasRef.current = canvas;

      // Éléments vidéo cachés rattachés aux flux de chaque participant.
      videosRef.current = sources
        .filter((s) => s.stream)
        .map((s) => {
          const el = document.createElement('video');
          el.muted = true;
          el.playsInline = true;
          el.autoplay = true;
          el.srcObject = s.stream;
          void el.play().catch(() => { /* autoplay bloqué : le canvas reste noir */ });
          return { el, name: s.name || 'Participant' };
        });

      // Mélange audio.
      const AudioCtxClass = window.AudioContext
        || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      let audioDest: MediaStreamAudioDestinationNode | null = null;
      if (AudioCtxClass) {
        const audioCtx = new AudioCtxClass();
        audioCtxRef.current = audioCtx;
        audioDest = audioCtx.createMediaStreamDestination();
        sources.forEach((s) => {
          if (!s.stream || s.stream.getAudioTracks().length === 0) return;
          try {
            const src = audioCtx.createMediaStreamSource(s.stream);
            src.connect(audioDest as MediaStreamAudioDestinationNode);
          } catch {
            /* flux audio indisponible */
          }
        });
      }

      const videoTrack = canvas.captureStream(25).getVideoTracks()[0];
      const tracks: MediaStreamTrack[] = [];
      if (videoTrack) tracks.push(videoTrack);
      if (audioDest) audioDest.stream.getAudioTracks().forEach((t) => tracks.push(t));
      const composed = new MediaStream(tracks);

      const mime = pickMime();
      const recorder = new MediaRecorder(composed, {
        mimeType: mime,
        videoBitsPerSecond: 2_500_000,
        audioBitsPerSecond: 128_000,
      });
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        const blob = chunksRef.current.length ? new Blob(chunksRef.current, { type: mime }) : null;
        cleanup();
        if (stopResolveRef.current) {
          stopResolveRef.current(blob);
          stopResolveRef.current = null;
        }
      };
      recorder.start(1000);
      recorderRef.current = recorder;
      setRecording(true);
      setElapsed(0);
      timerRef.current = window.setInterval(() => setElapsed((s) => s + 1), 1000);
      rafRef.current = requestAnimationFrame(drawFrame);
    } catch {
      setError("Impossible de démarrer l'enregistrement sur cet appareil.");
      cleanup();
    }
  }, [cleanup, drawFrame]);

  const stop = useCallback((): Promise<Blob | null> => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === 'inactive') {
      cleanup();
      return Promise.resolve(null);
    }
    return new Promise<Blob | null>((resolve) => {
      stopResolveRef.current = resolve;
      try {
        recorder.stop();
      } catch {
        cleanup();
        resolve(null);
      }
    });
  }, [cleanup]);

  const upload = useCallback(async (
    blob: Blob,
    userId: number,
    title: string,
    opts?: { roomId?: string; meetingId?: number | null; durationSeconds?: number },
  ): Promise<string | null> => {
    if (!blob || !userId) return null;
    try {
      const safe = (title || 'zcall').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'zcall';
      const stamp = new Date().toISOString().replace(/[:.]/g, '-');
      const buffer = new Uint8Array(await blob.arrayBuffer());
      const fileName = `${safe}-${stamp}.webm`;
      // Les enregistrements vivent dans le dossier « ZCall » de Mes médias.
      const url = await uploadToSeaweedFS(buffer, fileName, 'video/webm', `users/${userId}/ZCall`);
      // Historique : durée + taille conservées pour /dashboard/media.
      try {
        await supabase.from('zcall_recordings').insert({
          user_id: userId,
          room_id: opts?.roomId ?? null,
          meeting_id: opts?.meetingId ?? null,
          title: title || 'Réunion ZCall',
          url,
          storage_key: `users/${userId}/ZCall/${fileName}`,
          duration_seconds: Math.max(0, Math.round(opts?.durationSeconds ?? 0)),
          size_bytes: blob.size,
          created_at: new Date().toISOString(),
        });
      } catch {
        /* best-effort : le fichier reste disponible même sans métadonnées */
      }
      return url;
    } catch {
      return null;
    }
  }, []);

  return { recording, elapsed, error, start, stop, upload };
}